import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  $countriesStore,
  $recipientCreateStore,
  $recipientUpdateStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsUpdateStore,
  $transferRequestsGetFxRateStore,
  $recipientMandatoryFieldsStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils } from '@shared/utils';
import {
  AmountTypeEnum,
  TransferRecipientTypeEnum,
  TransferRequestTypeEnum,
  VopStatus,
  type AccountResponse,
  type RecipientResponse,
} from '@shared/api';
import {
  AccountFormBlock,
  AddressFormBlock,
  FilesFormBlock,
  PaymentFormBlock,
  PersonalFormBlock,
  RecipientSelectBlock,
  RecipientVerificationModal,
  TransferConfirmationBlock,
  VerifiedRecipientModal,
  type AccountFormData,
  type AddressFormData,
  type FilesFormData,
  type PaymentFormData,
  type PersonalFormData,
} from '@widgets';
import { Button, type FileWithId, type CustomFile, Spinner } from '@shared/widgets';

type IndividualBetweenFormData = PersonalFormData &
  AddressFormData &
  AccountFormData &
  PaymentFormData &
  FilesFormData & { recipient?: RecipientResponse | null };
const DEFAULT_VALUES: IndividualBetweenFormData = {
  firstName: '',
  lastName: '',
  idNumber: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  province: '',
  companyCountry: '',
  iban: '',
  currency: '',
  bankName: '',
  bankCountry: '',
  accountNumber: '',
  bic: '',
  accountType: undefined,
  bankProvince: '',
  bankAddress: '',
  bankCity: '',
  branchCode: '',
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  fastPayment: true,
  files: [] as (FileWithId | CustomFile)[],
};

interface OtherAccountIndividualTabProps {
  account: AccountResponse | null;
}

export const OtherAccountIndividualTab = (props: OtherAccountIndividualTabProps) => {
  const countriesStore = useUnit($countriesStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const recipientCreateStore = useUnit($recipientCreateStore.store);
  const recipientUpdateStore = useUnit($recipientUpdateStore.store);

  const [showRecipientDetails, setShowRecipientDetails] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [recipientData, setRecipientData] = useState<RecipientResponse>();

  const formId = useId();
  const formControl = useForm<IndividualBetweenFormData>({ defaultValues: DEFAULT_VALUES });
  const recipient = formControl.watch('recipient');
  const amount = formControl.watch('amount');
  const beneficiaryAmount = formControl.watch('beneficiaryAmount');
  const companyCountry = formControl.watch('companyCountry');
  const currency = formControl.watch('currency');
  const bankCountry = formControl.watch('bankCountry');
  const fastPayment = formControl.watch('fastPayment');

  const { t } = useTranslation('payments');

  useEffect(() => {
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsUpdateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (!recipient) return;
    setShowRecipientDetails(true);
  }, [recipient]);

  const requestFeeHandler = useCallback(() => {
    if (amount && (recipient?.bankCountryCode || bankCountry)) {
      $transferRequestsFeeStore.events.getFee({
        AccountId: props.account?.id,
        SenderAmount: amount,
        FastPayment: fastPayment,
        BeneficiaryCurrencyCode: recipient?.currency || currency,
        BeneficiaryBankCountryCode: recipient?.bankCountryCode || bankCountry,
        TransferRequestType: TransferRequestTypeEnum.OtherInsigniaAccount,
      });
    }
  }, [amount, bankCountry, currency, fastPayment, recipient, props.account?.id]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [props.account?.id, recipient, currency, bankCountry]);

  const countryOptions = useMemo(
    () => countriesStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })),
    [countriesStore],
  );
  useEffect(() => {
    const residenceCountryCode = countryOptions.find((option) => option.value === companyCountry)?.value;
    const bankCountryCode = countryOptions.find((option) => option.value === bankCountry)?.value;
    if (bankCountryCode && residenceCountryCode && currency) {
      $recipientMandatoryFieldsStore.events.getMandatoryFields({
        residenceCountryCode,
        bankCountryCode,
        currency,
        type: TransferRecipientTypeEnum.Individual,
      });
    }
  }, [bankCountry, companyCountry, countryOptions, currency]);
  useEffect(() => {
    if (props.account?.id && props.account?.currency && (recipient?.currency || currency) && (bankCountry || recipient?.bankCountryCode)) {
      if (props.account.currency === (recipient?.currency || currency)) {
        $transferRequestsGetFxRateStore.events.setRate(1);
        return;
      }
      $transferRequestsGetFxRateStore.events.getFxRate({
        AccountId: props.account?.id,
        FromCurrency: props.account?.currency,
        ToCurrency: recipient?.currency || currency,
        CountryCode: bankCountry || recipient?.bankCountryCode,
      });
    }
  }, [props.account, currency, bankCountry, formControl.formState.isValid, recipient]);

  const submitWrapper = async (data: IndividualBetweenFormData, vopForceUpdate?: boolean) => {
    let recipient = data.recipient;
    if (data.recipient == null)
      recipient = recipientData
        ? await PaymentUtils.updateRecipient({ ...data, id: recipientData.id, type: TransferRecipientTypeEnum.Individual, vopForceUpdate })
        : await PaymentUtils.createRecipient({ ...data, type: TransferRecipientTypeEnum.Individual, vopForceUpdate });
    if (!recipient || !props.account) return;
    setRecipientData(recipient);

    if (transferRequestsCreateStore?.data) {
      await PaymentUtils.updateTransfer({
        account: props.account,
        recipient,
        payment: { ...data, id: transferRequestsCreateStore.data.id, type: TransferRequestTypeEnum.OtherInsigniaAccount },
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        formControl.setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const transfer = await PaymentUtils.createTransfer({
      account: props.account,
      recipient,
      payment: { ...data, type: TransferRequestTypeEnum.OtherInsigniaAccount },
    });
    if (transfer.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transfer.data.id, files: data.files });
      formControl.setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const submitHandler = async (data: IndividualBetweenFormData) => await submitWrapper(data);
  const submitForceUpdateHandler = async () => await submitWrapper(formControl.getValues(), true);

  const reasonOptions = useMemo(() => {
    const bankCountryCode = (recipient ? recipient.bankCountryCode : bankCountry)?.toUpperCase();
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Individual));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data, bankCountry, recipient]);
  return (
    <FormProvider {...formControl}>
      <Spinner
        show={
          formControl.formState.isSubmitting ||
          recipientCreateStore.loading ||
          recipientUpdateStore.loading ||
          transferRequestsCreateStore.loading ||
          transferRequestsUpdateStore.loading
        }
        position
      />
      <form id={formId} noValidate onSubmit={formControl.handleSubmit(submitHandler)}>
        <div className='bg-white p-6 dark:bg-soft-black'>
          <Controller
            name='recipient'
            control={formControl.control}
            rules={{
              validate: (value) => {
                if (value === undefined) return t('errors.form.required', { ns: 'common' });
                return true;
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <RecipientSelectBlock
                type={TransferRecipientTypeEnum.Individual}
                isInternalAccount
                value={value}
                onChange={(recipient) => (onChange(recipient), formControl.setValue('purpose', ''))}
                error={error?.message}
                showRecipientDetails={showRecipientDetails}
                onToggleRecipientDetails={() => setShowRecipientDetails((prev) => !prev)}
              />
            )}
          />
          {recipient === null && (
            <div className='mt-6 flex flex-col gap-6'>
              <PersonalFormBlock />
              <AddressFormBlock />
              <AccountFormBlock />
            </div>
          )}
        </div>
        <PaymentFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
          account={props.account}
          currency={currency}
          reasonOptions={reasonOptions}
          onRequestFee={requestFeeHandler}
        />
        <FilesFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
          transactionId={transferRequestsCreateStore.data?.id}
        />
      </form>
      <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 rounded-b-xl bg-white p-6 pt-0 dark:bg-soft-black')}>
        <Button
          className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
          color='primary'
          type='submit'
          form={formId}
          disabled={feeStore.data === undefined || feeStore.loading || transferRequestsCreateStore.loading || formControl.formState.isSubmitting}
        >
          {t('form.buttons.transfer')} {AmountUtils.transformToAmountWithCurrency(beneficiaryAmount, recipient ? recipient.currency : currency)}
        </Button>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          showPaymentModal={
            showPaymentModal &&
            recipientCreateStore.data?.vopStatus !== VopStatus.Verified &&
            recipientUpdateStore.data?.vopStatus !== VopStatus.Verified
          }
          onClosePaymentModal={() => setShowPaymentModal(false)}
          account={props.account!}
          recipient={recipientData}
          vopForceUpdate={!recipient}
        />
      )}
      <VerifiedRecipientModal
        isOpen={recipientCreateStore.data?.vopStatus === VopStatus.Verified}
        onClose={() => ($recipientCreateStore.events.clear(), $recipientUpdateStore.events.clear())}
        description='A recipient was added'
        buttonTitle='Continue payment'
      />
      <RecipientVerificationModal
        isOpen={!!recipientCreateStore.error?.errorCode || !!recipientUpdateStore.error?.errorCode}
        onClose={() => ($recipientCreateStore.events.clear(), $recipientUpdateStore.events.clear())}
        type='createTransfer'
        error={recipientCreateStore.error || recipientUpdateStore.error}
        onSubmit={submitForceUpdateHandler}
      />
    </FormProvider>
  );
};
